import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import logo from "../assets/logo_720medialabs.png";
import "./Home.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

function getUser() {
  try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
}

const TYPE_LABELS = { bug: "Bug", suggestion: "Sugerencia", comment: "Comentario" };
const TYPE_COLORS = { bug: "bug", suggestion: "suggestion", comment: "comment" };

export default function Home({ can = () => false }) {
  const user = getUser();
  const isAdmin = can("admin.news");

  // News
  const [news, setNews] = useState([]);

  // Feedback
  const [feedbackList, setFeedbackList] = useState([]);
  const [fbMessage, setFbMessage] = useState("");
  const [fbType, setFbType] = useState("comment");
  const [sending, setSending] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const feedbackEndRef = useRef(null);

  // News form (admin only)
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [newsVersion, setNewsVersion] = useState("");
  const [newsTitle, setNewsTitle] = useState("");
  const [newsBody, setNewsBody] = useState("");
  const [creatingNews, setCreatingNews] = useState(false);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadNews = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/news`, { headers });
      const d = await res.json();
      setNews(d.news || []);
    } catch {}
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/feedback`, { headers });
      const d = await res.json();
      setFeedbackList(d.feedback || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadNews();
    loadFeedback();
  }, [loadNews, loadFeedback]);

  // Archive feedback
  const handleArchiveFeedback = async (fbId) => {
    try {
      const res = await fetch(`${API_URL}/feedback/${fbId}/archive`, {
        method: "PATCH",
        headers,
      });
      if (res.ok) await loadFeedback();
    } catch {}
  };

  // Send feedback
  const handleSendFeedback = async () => {
    if (!fbMessage.trim() || sending) return;
    setSending(true);
    setErrorMsg("");
    try {
      const form = new FormData();
      form.append("message", fbMessage.trim());
      form.append("fb_type", fbType);
      const res = await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (res.ok) {
        setFbMessage("");
        setThanks(true);
        setTimeout(() => setThanks(false), 3000);
        await loadFeedback();
        setTimeout(() => feedbackEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        const err = await res.json().catch(() => ({}));
        setErrorMsg(err.detail || `Error ${res.status}`);
      }
    } catch (err) {
      setErrorMsg(err.message || "Error de conexión");
    }
    setSending(false);
  };

  // Create or update news (admin)
  const handleSaveNews = async () => {
    if (!newsTitle.trim() || !newsVersion.trim() || creatingNews) return;
    setCreatingNews(true);
    try {
      const form = new FormData();
      form.append("version", newsVersion.trim());
      form.append("title", newsTitle.trim());
      form.append("body", newsBody.trim());
      const url = editingNewsId ? `${API_URL}/news/${editingNewsId}` : `${API_URL}/news`;
      const method = editingNewsId ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (res.ok) {
        setNewsVersion("");
        setNewsTitle("");
        setNewsBody("");
        setEditingNewsId(null);
        setShowNewsForm(false);
        await loadNews();
      }
    } catch {}
    setCreatingNews(false);
  };

  // Edit news — load into form
  const handleEditNews = (item) => {
    setEditingNewsId(item.id);
    setNewsVersion(item.version);
    setNewsTitle(item.title);
    setNewsBody(item.body || "");
    setShowNewsForm(true);
  };

  // Delete news
  const handleDeleteNews = async (item) => {
    try {
      const res = await fetch(`${API_URL}/news/${item.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) await loadNews();
    } catch {}
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="home-page">
      {/* Hero — logo grande */}
      <div className="home-hero">
        <img src={logo} alt="720 Media Labs" className="home-hero__logo" />
        <h1 className="home-hero__title">720 Media Labs Tools</h1>
        <span className="home-hero__version">v1.25</span>
      </div>

      {/* Contenido 2 columnas */}
      <div className="home-content">
        {/* LEFT — News */}
        <div className="home-panel home-panel--news">
          <div className="home-panel__header">
            <h2 className="home-panel__title">Novedades</h2>
            {isAdmin && (
              <button className="home-btn-add" onClick={() => {
                if (showNewsForm) { setShowNewsForm(false); setEditingNewsId(null); setNewsVersion(""); setNewsTitle(""); setNewsBody(""); }
                else setShowNewsForm(true);
              }}>
                {showNewsForm ? "Cancelar" : "+ Publicar"}
              </button>
            )}
          </div>

          {/* Admin: create news form */}
          {showNewsForm && (
            <div className="home-news-form">
              <div className="home-news-form__row">
                <input
                  className="home-news-form__input home-news-form__input--version"
                  placeholder="v1.25"
                  value={newsVersion}
                  onChange={(e) => setNewsVersion(e.target.value)}
                />
                <input
                  className="home-news-form__input"
                  placeholder="Titulo del anuncio..."
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                />
              </div>
              <textarea
                className="home-news-form__textarea"
                placeholder="Describe las novedades..."
                value={newsBody}
                onChange={(e) => setNewsBody(e.target.value)}
                rows={3}
              />
              <button
                className="home-news-form__btn"
                onClick={handleSaveNews}
                disabled={!newsTitle.trim() || !newsVersion.trim() || creatingNews}
              >
                {creatingNews ? "Guardando..." : editingNewsId ? "Guardar cambios" : "Publicar"}
              </button>
            </div>
          )}

          {/* News list */}
          <div className="home-news-list">
            {news.length > 0 ? news.map((item) => (
              <div key={item.id} className="home-news-item">
                <div className="home-news-item__header">
                  <span className="home-news-item__version">{item.version}</span>
                  <span className="home-news-item__title">{item.title}</span>
                  <span className="home-news-item__date">{formatDate(item.created_at)}</span>
                </div>
                {isAdmin && (
                  <span className="home-news-item__actions">
                    <button className="home-news-item__btn" onClick={() => handleEditNews(item)} title="Editar">&#9998;</button>
                    <button className="home-news-item__btn home-news-item__btn--del" onClick={() => handleDeleteNews(item)} title="Eliminar">&times;</button>
                  </span>
                )}
                {item.body && <p className="home-news-item__body">{item.body}</p>}
              </div>
            )) : (
              <div className="home-empty">Sin novedades por ahora</div>
            )}
          </div>
        </div>

        {/* RIGHT — Feedback */}
        <div className="home-panel home-panel--feedback">
          <h2 className="home-panel__title">Feedback</h2>

          {/* Input */}
          <div className="home-fb-input">
            <textarea
              className="home-fb-input__textarea"
              placeholder="Cuéntanos tu experiencia, reporta un bug o sugiere mejoras..."
              value={fbMessage}
              onChange={(e) => setFbMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && fbMessage.trim()) { e.preventDefault(); handleSendFeedback(); } }}
              rows={2}
            />
            <div className="home-fb-input__actions">
              <select
                className="home-fb-input__type"
                value={fbType}
                onChange={(e) => setFbType(e.target.value)}
              >
                <option value="comment">Comentario</option>
                <option value="bug">Bug</option>
                <option value="suggestion">Sugerencia</option>
              </select>
              <button
                className="home-fb-input__send"
                onClick={handleSendFeedback}
                disabled={!fbMessage.trim() || sending}
              >
                {sending ? "Enviando..." : "Enviar"}
              </button>
            </div>
            {thanks && <div className="home-fb-thanks">Gracias por tu feedback!</div>}
            {errorMsg && <div className="cs-error" style={{marginTop: "0.3rem", fontSize: "0.8rem"}}>{errorMsg}</div>}
          </div>

          {/* Issues list */}
          <div className="home-fb-list">
            {feedbackList.length > 0 ? feedbackList.map((fb) => (
              <div key={fb.id} className="home-fb-item">
                <div className="home-fb-item__header">
                  <span className="home-fb-item__user">{fb.user_name}</span>
                  <span className={`home-fb-item__type home-fb-item__type--${TYPE_COLORS[fb.type] || "comment"}`}>
                    {TYPE_LABELS[fb.type] || fb.type}
                  </span>
                  <span className="home-fb-item__date">{formatDate(fb.created_at)}</span>
                  {(fb.user_id === user?.id || isAdmin) && (
                    <button
                      className="home-fb-item__archive"
                      onClick={() => handleArchiveFeedback(fb.id)}
                      title="Archivar"
                    >&#x2715;</button>
                  )}
                </div>
                <p className="home-fb-item__message">{fb.message}</p>
              </div>
            )) : (
              <div className="home-empty">Nadie ha enviado feedback aun. Se el primero!</div>
            )}
            <div ref={feedbackEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
